import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: \"Erik Ashdown: “Why I’m excited to join Covalent”\"\ndate: 2020-10-30T04:25:15-08:00\ncategories: [\"company\"]\ndescription: \"Erik joins Covalent to lead Ecosystem Growth\"\nauthor: erik\nsubbrand_image: https://www.covalenthq.com/static/images/blog/202010/erik-joins-covalent.jpeg\n\n\ntype: legacy\n---\n\n## \"I don't think the market is big enough for your company…\"\n\nThat's what I told Ganesh Swami when we met in a coffee shop back in August 2018 after hearing about Covalent for the first time.\n\n\"Maybe it isn't, but it will be...\" was Ganesh's response.\n\n\n\nAt the time, I wasn't sure how one would grow a SQL database that indexes public blockchain data.\n\n* Who is the ideal customer?\n* Why should anyone care?\n* How long will it take this market to mature in order to be relevant?\n* What would be the catalyst to accelerate Covalent?\n\nI didn't know, neither did he. We parted ways but agreed to keep in touch, which we did.\n\n## \"We need to chat. When is a good time?\"\n\nFast forward to September 2020.\n\nI opened my Telegram app and saw a message from Ganesh: \"We need to chat. When is a good time?\"\n\n\nWe met at a coffee shop in East Vancouver. He proceeded to tell me about how they nearly shuttered after almost two years, and they now have a list of impressive clients both within the crypto space as well as clients outside of crypto. In addition, he went on to share how [Covalent had raised an oversubscribed round](/blog/covalent-funding-oct-2020/) of $3.1M USD in a private sale round.\n\nTo quote Owen Wilson, \"Wow\" 🤯\n\nI sit there in awe as he tells me this story, remembering what I had said in August 2018, \"I don't think the market is big enough for your company\". Not regretting anything I said but genuinely happy that someone I know, and respect had answered the tough questions and was now starting to scale his business. Ganesh proceeded to tell me multiple stories about things that are happening as I listened, excitedly.\n\nHaving digested this conversation, I knew there was no exaggeration because Ganesh has always been a man of integrity, both humble and pragmatic. In crypto, a rising tide raises all ships so DeFi growth has been universally good for everyone in the space, especially people looking to learn more about layer 1 or layer 2 chain action.\n\nAt a certain point he stops and says, \"So how much time can you commit to this?\" I laughed and smiled. He didn't know that I parted ways with [Bidali](https://www.bidali.com/) three weeks earlier and had been evaluating opportunities just days prior. I told him that I would get back to him.\n\nAs I started to reflect, it became obvious how much of a misnomer the accessibility of public blockchain data is. Yes, it is public, it is visible, but it is near inaccessible for a non-technical user since it requires an understanding as to how to set up graphs, aggregate data, index, accordingly even before visualizing anything.\n\nCovalent has unlocked the potential to pull data from a public blockchain in real-time. The fact that there is now a way to index public blockchain governance/non fungible token activity and calculate taxes on decentralized finance platforms is a very powerful thing. By making the data accessible, people will find a myriad of ways to make it useful and innovate in ways that have not been seen yet in this space. This gives people access to data in a way that is familiar. This is crucial.\n\nI sit here today, one week after joining the Covalent team, excited. Excited to work with exceptionally smart people, with an exceptionally strong vision. The cherry on top? This came from Vancouver, Canada from a person I know and respect.\n\n\nThanks Ganesh. I'm happy to be part of telling this story!\n\n\n\n\n";
const filepath = "/blog/erik-joins-covalent.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
